import type {Filter} from '@/types/search/filter';
import type {Pagination} from '@/types/search/pagination';
import type {TooltipObject} from '@/types/search/result';
import type {SelectedFilter} from '@/types/search/selected.filter';
import type {TableSort} from '@/types/table';
import {defineStore} from 'pinia';
import {type Ref, ref} from 'vue';
import {
	appendToUrlParams,
	getQueryParam,
	removeFromUrlParams,
	removeUrlParam,
	updateUrlParam
} from '@/utils/url.param.utils';
import {UrlParam} from '@/utils/url.param';
import {useAppStore} from '@/store/app.store';

export const useFilters = defineStore('filter-store', () => {

	const searchTerm: Ref<string> = ref(getQueryParam(UrlParam.SEARCH_KEY) ?? '');
	const filters: Ref<Array<Filter>> = ref([] as Array<Filter>);
	const filtersMinor: Ref<Array<Filter>> = ref([] as Array<Filter>);
	const pagination: Ref<Pagination | null> = ref(null);
	const sort: Ref<TableSort | null> = ref(null);
	const tooltips: Ref<TooltipObject | null> = ref(null);
	const expandedFilterName: Ref<string | null> = ref(null);
	const hideUnavailableFilter: Ref<boolean | undefined> = ref(getQueryParam(UrlParam.UNAVAILABLE_KEY) ?? true);
	const surplusFilter: Ref<boolean> = ref(getQueryParam(UrlParam.SURPLUS_KEY) ?? false);
	const selectedFilters: Ref<Array<SelectedFilter>> = ref([]);
	const selectedProducts: Ref<Array<string>> = ref(getQueryParam(UrlParam.VARIETY_ID_KEY) ?? []);

	const expandedFilter = (): Filter | undefined => {
		if (!expandedFilterName.value || !filters.value || !filtersMinor.value) {
			return undefined;
		}

		const expandedFilterMajor = filters.value.find(filter => filter.name === expandedFilterName.value);
		const expandedFilterMinor = filtersMinor.value.find((filtersMinor: Filter) => filtersMinor.name === expandedFilterName.value);

		// Check if the previously expanded filter is still of type 'EXPAND'. The type can change
		// when checking filter options.
		if (expandedFilterMajor?.configuration?.type === 'EXPAND') {
			return expandedFilterMajor;
		}

		if (expandedFilterMinor?.configuration?.type === 'EXPAND') {
			return expandedFilterMinor;
		}

		return undefined;
	};

	const setSort = (newSort: TableSort) => {
		sort.value = newSort;

		if (sort.value?.field && sort.value?.direction) {
			updateUrlParam(UrlParam.SORT_KEY, `${sort.value.field},${sort.value.direction}`);
		}
	};

	const setActivePage = (page?: number) => {
		pagination.value = {...pagination.value, activePage: page};
		updateUrlParam(UrlParam.PAGE_KEY, pagination.value?.activePage);
	};

	const setSearchTerm = (value: string) => {
		if (value) {
			searchTerm.value = value;
			setSort({field: '_score', direction: 'desc'});
		} else {
			searchTerm.value = '';
			setSort({field: 'name', direction: 'asc'});
		}

		setActivePage(0);
		updateUrlParam(UrlParam.SEARCH_KEY, searchTerm.value);
	};

	const setPageSize = (pageSize?: number) => {
		pagination.value = {...pagination.value, pageSize};
		updateUrlParam(UrlParam.PAGE_SIZE_KEY, pagination.value?.pageSize);
	};

	const setTotalPages = (totalPages?: number) => {
		pagination.value = {...pagination.value, totalPages};
	};

	const setPagination = (newPagination: Pagination) => {
		setTotalPages(newPagination.totalPages);
		setActivePage(newPagination.activePage);
		setPageSize(newPagination.pageSize);
	};

	const setSurplus = (value: boolean) => {
		surplusFilter.value = value;
		setActivePage(0);
		updateUrlParam(UrlParam.SURPLUS_KEY, surplusFilter.value);
	};

	const setHideUnavailable = (value: boolean | undefined) => {
		hideUnavailableFilter.value = value;
		setActivePage(0);
		updateUrlParam(UrlParam.UNAVAILABLE_KEY, hideUnavailableFilter.value);
	};

	const resetExpandedFilter = (): void => {
		expandedFilterName.value = null;
		useAppStore().setFilterExpanded(false);
	};

	const collapseActiveFilter = () => {
		if (expandedFilter()) {
			resetExpandedFilter();
		}
	};

	const setExpandedFilterName = (filterName: string) => {
		expandedFilterName.value = filterName;
		useAppStore().setFilterExpanded(true);
	};

	const updateSelectedFilters = (filterName: string, newValue: string): void => {
		const index = selectedFilters.value.findIndex(filter =>
			filter.name === filterName && filter.value === newValue);

		if (index > -1) {
			selectedFilters.value.splice(index, 1);
			removeFromUrlParams(filterName, newValue);
		} else {
			selectedFilters.value.push({name: filterName, value: newValue});
			appendToUrlParams(filterName, newValue);
		}

		setActivePage(0);
	};

	const resetSelectedFilters = () => {
		selectedFilters.value.forEach(filter => removeUrlParam(filter.name));
		selectedFilters.value = [];
	};

	const resetSelectedProducts = () => {
		removeUrlParam(UrlParam.VARIETY_ID_KEY);
		selectedProducts.value = [];
	}

	const setFilterArrays = (newFilters: Array<Filter>) => {
		// Split before the filter 'Focus'
		const splitIndex = newFilters.findIndex(filter => filter.name === 'focus');

		// Remove filters without options and the concepts and segment filters.
		const filterCondition = (filter: Filter) =>
			filter.options.length > 0 &&
			filter.name !== 'concepts' &&
			filter.name !== 'segment';

		filters.value = newFilters.slice(0, splitIndex).filter(filterCondition);
		filtersMinor.value = newFilters.slice(splitIndex).filter(filterCondition);
	};

	const setFilters = (newFilters: Array<Filter>) => {
		selectedFilters.value = [];

		newFilters.forEach(filter => filter.options.forEach((option) => {
			if (option.active) {
				updateSelectedFilters(filter.name, option.value);
			}

		}));

		setFilterArrays(newFilters);
	};

	const getOptions = (name: string) => {
		const filter = filters.value.find(filter => filter.name === name);

		return filter ? filter.options : [];
	};

	const getOptionsMinor = (name: string) => {
		const filter = filtersMinor.value.find(filter => filter.name === name);

		return filter ? filter.options : [];
	};

	const resetFilters = () => {
		setSearchTerm('');
		setActivePage(0);
		setSurplus(false);
		setHideUnavailable(undefined);
		resetSelectedFilters();
		resetSelectedProducts();
	};

	return {
		searchTerm,
		sort,
		pagination,
		tooltips,
		expandedFilterName,
		filters,
		filtersMinor,
		surplusFilter,
		selectedFilters,
		hideUnavailableFilter,
		selectedProducts,
		expandedFilter,
		setSearchTerm,
		setSort,
		setActivePage,
		setPageSize,
		setPagination,
		setFilters,
		setSurplus,
		setHideUnavailable,
		resetExpandedFilter,
		updateSelectedFilters,
		getOptions,
		getOptionsMinor,
		setExpandedFilterName,
		resetFilters,
		collapseActiveFilter
	};
});
